import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { buildAiResponse, parseScheduleText } from "../services/mockAi";
import { createDemoSeed } from "../services/demoSeed";
import { getScopeConnections } from "../services/demoSelectors";
import type {
  AnalysisScope,
  AppTheme,
  ChatMessage,
  ChartType,
  ConnectionId,
  Connection,
  DemoState,
  Group,
  GroupId,
  InlineChart,
  PermissionLevel,
  PlatformType,
  ScheduleReport,
  TeamInvite,
  UserRole,
} from "../types/demo";

interface DemoContextValue {
  state: DemoState;
  currentUser: DemoState["users"][number];
  activeTheme: AppTheme;
  loginAsRole: (role: UserRole) => void;
  logout: () => void;
  resetDemo: () => void;
  setPendingPrompt: (prompt: string) => void;
  setSelectedRange: (range: DemoState["selectedRange"]) => void;
  clearChat: (threadId?: string) => void;
  askAi: (prompt: string, scope: AnalysisScope, threadId?: string, regenerate?: boolean) => string;
  toggleFavorite: (threadId: string, messageId: string, prompt: string, response: string) => void;
  convertPromptToReport: (prompt: string) => void;
  saveReport: (input: Omit<ScheduleReport, "id" | "createdAt" | "nextRunAt" | "scheduleRule"> & { id?: string }) => void;
  deleteReport: (id: string) => void;
  saveInvite: (email: string, permissions: Record<string, PermissionLevel[]>) => void;
  cancelInvite: (id: string) => void;
  connectPlatform: (platform: PlatformType, connectionName: string) => Promise<void>;
  completeOAuthConnection: (input: {
    platform: Extract<PlatformType, "xero">;
    name: string;
    tenantId: string;
    tenantName?: string;
    tenantType?: string;
    oauth: NonNullable<Connection["oauth"]>;
  }) => Promise<Connection>;
  syncConnection: (id: string) => Promise<void>;
  renameConnection: (id: string, name: string) => Promise<void>;
  disconnectConnection: (id: string) => Promise<void>;
  saveGroup: (input: { id?: GroupId; name: string; description?: string; connectionIds: ConnectionId[] }) => void;
  deleteGroup: (id: GroupId) => void;
  deleteWaitlist: (id: string) => void;
  deleteUser: (id: string) => void;
  toggleUserStatus: (id: string) => void;
  setTheme: (theme: AppTheme) => void;
}

const storageKey = "quantira-demo-state-v1";
const storedConnectorPlatforms: PlatformType[] = ["procore", "xero"];

const isValidScope = (scope: AnalysisScope, state: DemoState): boolean => {
  if (scope === "all") {
    return true;
  }

  return state.groups.some(group => group.id === scope) || state.connections.some(connection => connection.id === scope);
};

const normalizeScope = (scope: AnalysisScope, state: DemoState): AnalysisScope => {
  if (isValidScope(scope, state)) {
    return scope;
  }

  return "all";
};

const xeroRefreshBufferMs = 5 * 60 * 1000;

const shouldRefreshXeroOauth = (connection: Connection): boolean => {
  if (connection.platform !== "xero" || !connection.oauth?.refreshToken) {
    return false;
  }

  const expiresAt = connection.oauth.expiresAt;
  if (!expiresAt) {
    return true;
  }

  const expiryTime = Date.parse(expiresAt);
  if (Number.isNaN(expiryTime)) {
    return true;
  }

  return expiryTime - Date.now() <= xeroRefreshBufferMs;
};

const loadState = (): DemoState => {
  const raw = localStorage.getItem(storageKey);
  if (!raw) return createDemoSeed();

  try {
    const parsed = JSON.parse(raw) as Partial<DemoState>;
    const seed = createDemoSeed();

    const merged = {
      ...seed,
      ...parsed,
      activeTheme: parsed.activeTheme ?? "obsidian",
      groups: parsed.groups ?? seed.groups,
      connections: (parsed.connections ?? seed.connections).map(connection => {
        const legacy = connection as Connection & { groupId?: GroupId };
        const migratedGroupIds = Array.isArray(legacy.groupIds)
          ? legacy.groupIds
          : legacy.groupId
            ? [legacy.groupId]
            : [];
        return {
          ...legacy,
          groupIds: migratedGroupIds,
        };
      }),
      chatThreads: parsed.chatThreads ?? seed.chatThreads,
      scheduledReports: parsed.scheduledReports ?? seed.scheduledReports,
    } as DemoState;

    return {
      ...merged,
      scheduledReports: merged.scheduledReports.map(report => ({
        ...report,
        scope: normalizeScope(report.scope, merged),
      })),
      chatThreads: merged.chatThreads.map(thread => ({
        ...thread,
        scope: normalizeScope(thread.scope, merged),
      })),
    };
  } catch {
    return createDemoSeed();
  }
};

const DemoContext = createContext<DemoContextValue | null>(null);

export const DemoProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<DemoState>(() => loadState());

  const syncStoredConnections = async () => {
    try {
      const response = await fetch("/api/integrations/connections");

      if (!response.ok) {
        return;
      }

      const payload = (await response.json()) as { connections?: Connection[] };
      const nextConnections = payload.connections ?? [];

      update(prev => ({
        ...prev,
          connections: [
          ...prev.connections.filter(connection => !storedConnectorPlatforms.includes(connection.platform)),
          ...nextConnections,
        ],
      }));
    } catch {
      return;
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", state.activeTheme);
  }, [state.activeTheme]);

  useEffect(() => {
    void syncStoredConnections();
  }, [state.currentUserId]);

  const persist = (next: DemoState) => {
    setState(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const update = (updater: (prev: DemoState) => DemoState) => {
    setState(prev => {
      const next = updater(prev);
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const currentUser = state.users.find(user => user.id === state.currentUserId) ?? createDemoSeed().users[0]!;

  const loginAsRole = (role: UserRole) => {
    const nextUser = state.users.find(user => user.role === role);
    if (!nextUser) return;
    persist({ ...state, currentUserId: nextUser.id });
  };

  const logout = () => {
    const defaultUserId = createDemoSeed().users[0]?.id;
    if (!defaultUserId) {
      return;
    }

    update(prev => ({
      ...prev,
      currentUserId: defaultUserId,
      pendingPrompt: "",
    }));
  };

  const resetDemo = () => {
    persist(createDemoSeed());
  };

  const setPendingPrompt = (prompt: string) => {
    update(prev => ({ ...prev, pendingPrompt: prompt }));
  };

  const setSelectedRange = (range: DemoState["selectedRange"]) => {
    update(prev => ({ ...prev, selectedRange: range }));
  };

  const clearChat = (threadId?: string) => {
    update(prev => {
      if (!threadId) {
        return {
          ...prev,
          pendingPrompt: "",
          chatThreads: prev.chatThreads.map(thread => ({
            ...thread,
            title: "New conversation",
            messages: [],
            updatedAt: new Date().toISOString(),
          })),
          favorites: [],
        };
      }

      return {
        ...prev,
        pendingPrompt: "",
        chatThreads: prev.chatThreads.map(thread =>
          thread.id === threadId
            ? {
                ...thread,
                title: "New conversation",
                messages: [],
                updatedAt: new Date().toISOString(),
              }
            : thread,
        ),
        favorites: prev.favorites.filter(item => item.threadId !== threadId),
      };
    });
  };

  const resolveThreadHistory = (threadId: string): Array<{ role: "user" | "assistant"; content: string }> => {
    const thread = state.chatThreads.find(item => item.id === threadId);
    if (!thread) {
      return [];
    }

    return thread.messages
      .filter(item => item.role === "user" || item.role === "assistant")
      .map(item => ({ role: item.role, content: item.content }))
      .slice(-8);
  };

  const updateAssistantMessage = (threadId: string, messageId: string, updater: (message: ChatMessage) => ChatMessage) => {
    update(prev => {
      const nextThreads = [...prev.chatThreads];
      const thread = nextThreads.find(item => item.id === threadId);
      if (!thread) {
        return prev;
      }

      thread.messages = thread.messages.map(message => {
        if (message.id !== messageId) {
          return message;
        }
        return updater(message);
      });
      thread.updatedAt = new Date().toISOString();

      return {
        ...prev,
        chatThreads: nextThreads,
      };
    });
  };

  const refreshScopedXeroConnections = async (baseState: DemoState, scope: AnalysisScope): Promise<DemoState> => {
    const scopedIds = new Set(
      getScopeConnections(baseState, scope)
        .filter(connection => shouldRefreshXeroOauth(connection))
        .map(connection => connection.id),
    );

    if (scopedIds.size === 0) {
      return baseState;
    }

    let nextState = baseState;
    let didChange = false;

    for (const connection of baseState.connections) {
      if (!scopedIds.has(connection.id) || !connection.oauth) {
        continue;
      }

      try {
        const response = await fetch("/api/integrations/xero/refresh", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tokenData: {
              access_token: connection.oauth.accessToken,
              refresh_token: connection.oauth.refreshToken,
              expires_at: connection.oauth.expiresAt,
              scope: connection.oauth.scope,
              token_type: connection.oauth.tokenType,
              id_token: connection.oauth.idToken,
            },
          }),
        });

        if (!response.ok) {
          continue;
        }

        const payload = (await response.json()) as {
          tokenData?: {
            access_token: string;
            refresh_token?: string;
            expires_at?: string;
            scope?: string;
            token_type?: string;
            id_token?: string;
          };
        };

        if (!payload.tokenData?.access_token) {
          continue;
        }

        didChange = true;
        nextState = {
          ...nextState,
          connections: nextState.connections.map(item => {
            if (item.id !== connection.id || !item.oauth) {
              return item;
            }

            return {
              ...item,
              oauth: {
                accessToken: payload.tokenData?.access_token ?? item.oauth.accessToken,
                refreshToken: payload.tokenData?.refresh_token ?? item.oauth.refreshToken,
                expiresAt: payload.tokenData?.expires_at,
                scope: payload.tokenData?.scope ?? item.oauth.scope,
                tokenType: payload.tokenData?.token_type ?? item.oauth.tokenType,
                idToken: payload.tokenData?.id_token ?? item.oauth.idToken,
              },
            };
          }),
        };
      } catch {
        continue;
      }
    }

    if (didChange) {
      persist(nextState);
    }

    return nextState;
  };

  const finalizeAssistantMessage = (
    threadId: string,
    messageId: string,
    prompt: string,
    response: ReturnType<typeof buildAiResponse>,
  ) => {
    updateAssistantMessage(threadId, messageId, message => ({
      ...message,
      content: response.body,
      response,
      isStreaming: false,
    }));

    update(prev => {
      const now = new Date().toISOString();

      return {
        ...prev,
        auditEvents: [
          {
            id: `audit-${crypto.randomUUID()}`,
            username: currentUser.name,
            prompt,
            responseSnippet: response.body.slice(0, 90),
            ipAddress: "203.14.67.211",
            occurredAt: now,
          },
          ...prev.auditEvents,
        ],
      };
    });
  };

  const requestAgentReply = async (
    prompt: string,
    scope: AnalysisScope,
    threadId: string,
    assistantMessageId: string,
    regenerate: boolean,
    history: Array<{ role: "user" | "assistant"; content: string }>,
  ) => {
    type AgentWsEvent =
      | { type: "status"; requestId: string; status: string }
      | { type: "tool_call"; requestId: string; name: string; args: Record<string, unknown> }
      | { type: "tool_result"; requestId: string; name: string; summary: string }
      | { type: "content_chunk"; requestId: string; chunk: string }
      | { type: "complete"; requestId: string; response: ReturnType<typeof buildAiResponse> }
      | { type: "error"; requestId: string; error: string };

    let effectiveState = state;

    const fallback = () => {
      const fallbackResponse = buildAiResponse(effectiveState, prompt, scope, regenerate ? 1 : 0);
      finalizeAssistantMessage(threadId, assistantMessageId, prompt, fallbackResponse);
    };

    effectiveState = await refreshScopedXeroConnections(effectiveState, scope);
    const scopedConnections = getScopeConnections(effectiveState, scope);
    const requestId = `req-${crypto.randomUUID()}`;
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${protocol}://${window.location.host}/api/agent/ws`);
    let completed = false;
    const toolCallConnectorEventQueue = new Map<string, string[][]>();

    const normalizeEventLabel = (label: string): string => {
      if (label.startsWith("Model reasoning pass")) {
        return "Thinking through your request";
      }
      if (label === "Analyzing request") {
        return "Reviewing your request";
      }
      if (label === "Drafting final response") {
        return "Writing your answer";
      }
      if (label === "Completed") {
        return "Done";
      }
      return label;
    };

    const pushEvent = (nextLabel: string) => {
      updateAssistantMessage(threadId, assistantMessageId, message => {
        const last = message.toolEvents?.[message.toolEvents.length - 1];
        if (last === nextLabel) {
          return message;
        }
        return {
          ...message,
          toolEvents: [...(message.toolEvents ?? []), nextLabel].slice(-6),
        };
      });
    };

    const toDateRange = (args: Record<string, unknown>): string => {
      const fromDate = typeof args.fromDate === "string" ? args.fromDate : "";
      const toDate = typeof args.toDate === "string" ? args.toDate : "";
      if (fromDate && toDate) {
        return `${fromDate} to ${toDate}`;
      }
      const weeks = typeof args.weeks === "number" ? args.weeks : undefined;
      if (weeks) {
        return `last ${weeks} week${weeks === 1 ? "" : "s"}`;
      }
      return "selected period";
    };

    const getToolDescription = (name: string, args: Record<string, unknown>): string => {
      if (name === "get_xero_invoices") {
        const minimumDaysOverdue = typeof args.minimumDaysOverdue === "number" ? args.minimumDaysOverdue : undefined;
        if (minimumDaysOverdue) {
          return `Fetching Xero invoices overdue by ${minimumDaysOverdue}+ days.`;
        }
        return "Fetching Xero invoices for the selected filters.";
      }
      if (name === "get_xero_cash_summary") {
        return "Fetching Xero cash summary to analyze inflows and outflows.";
      }
      if (name === "get_procore_projects") {
        const burnRisk = typeof args.burnRisk === "string" ? args.burnRisk : "all";
        return `Fetching Procore projects with ${burnRisk} burn-risk focus.`;
      }
      if (name === "get_procore_budget_line_items") {
        const projectId = typeof args.projectId === "string" ? args.projectId : "unknown";
        return `Fetching Procore budget line items for ${projectId}.`;
      }
      return `Fetching data via ${name}.`;
    };

    const getToolQuery = (name: string, args: Record<string, unknown>): string => {
      if (name === "get_xero_invoices") {
        const statuses = Array.isArray(args.statuses) ? args.statuses.map(item => String(item).toUpperCase()).join(",") : "ANY";
        const minimumDaysOverdue = typeof args.minimumDaysOverdue === "number" ? args.minimumDaysOverdue : 0;
        return `invoices?statuses=${statuses}&minDaysOverdue=${minimumDaysOverdue}`;
      }
      if (name === "get_xero_cash_summary") {
        return `cash-summary?range=${toDateRange(args).replace(/\s+/g, "-").toLowerCase()}`;
      }
      if (name === "get_procore_projects") {
        const burnRisk = typeof args.burnRisk === "string" ? args.burnRisk : "all";
        const manager = typeof args.manager === "string" ? args.manager : "any";
        return `projects?burnRisk=${burnRisk}&manager=${manager}`;
      }
      if (name === "get_procore_budget_line_items") {
        const projectId = typeof args.projectId === "string" ? args.projectId : "unknown";
        return `budget-line-items?projectId=${projectId}`;
      }
      return `${name}?scope=selected`;
    };

    const toolPlatform = (name: string): PlatformType | null => {
      if (name.startsWith("get_xero_")) {
        return "xero";
      }
      if (name.startsWith("get_procore_")) {
        return "procore";
      }
      return null;
    };

    const updateConnectorsForToolCall = (name: string, args: Record<string, unknown>) => {
      const platform = toolPlatform(name);
      if (!platform) {
        return;
      }

      const description = getToolDescription(name, args);
      const query = getToolQuery(name, args);
      const matchingConnections = scopedConnections.filter(connection => connection.platform === platform);

      if (matchingConnections.length === 0) {
        return;
      }

      const callKey = `call-${crypto.randomUUID()}`;
      const eventIds: string[] = [];

      const queue = toolCallConnectorEventQueue.get(name) ?? [];

      updateAssistantMessage(threadId, assistantMessageId, message => {
        const next = [...(message.connectorEvents ?? [])];

        matchingConnections.forEach(connection => {
          const eventId = `conn-event-${crypto.randomUUID()}`;
          eventIds.push(eventId);
          next.push({
            id: eventId,
            callKey,
            label: connection.name,
            status: "running",
            description,
            query,
          });
        });

        return {
          ...message,
          connectorEvents: next,
        };
      });

      queue.push(eventIds);
      toolCallConnectorEventQueue.set(name, queue);
    };

    const updateConnectorsForToolResult = (name: string, summary: string) => {
      const queue = toolCallConnectorEventQueue.get(name);
      if (!queue || queue.length === 0) {
        return;
      }

      const eventIds = queue.shift() ?? [];
      toolCallConnectorEventQueue.set(name, queue);

      if (eventIds.length === 0) {
        return;
      }

      const eventIdSet = new Set(eventIds);

      updateAssistantMessage(threadId, assistantMessageId, message => ({
        ...message,
        connectorEvents: (message.connectorEvents ?? []).map(item => {
          if (!eventIdSet.has(item.id)) {
            return item;
          }

          return {
            ...item,
            status: "success",
            summary,
          };
        }),
      }));
    };

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          type: "query",
          requestId,
          payload: {
            prompt,
            scope,
            state: effectiveState,
            history,
          },
        }),
      );
    };

    ws.onmessage = event => {
      let data: AgentWsEvent;
      try {
        data = JSON.parse(String(event.data)) as AgentWsEvent;
      } catch {
        return;
      }

      if (data.requestId !== requestId) {
        return;
      }

      if (data.type === "status") {
        pushEvent(normalizeEventLabel(data.status));
        return;
      }

      if (data.type === "tool_call") {
        if (data.name === "render_chart") {
          const chartType = String(data.args.type ?? "bar") as ChartType;
          const title = String(data.args.title ?? "Chart");
          const rawData = Array.isArray(data.args.data) ? data.args.data : [];
          const chartData = (rawData as Array<{ label?: unknown; value?: unknown }>)
            .map(item => ({ label: String(item.label ?? ""), value: Number(item.value ?? 0) }))
            .filter(item => item.label && Number.isFinite(item.value))
            .slice(0, 12);

          const chart: InlineChart = {
            id: `chart-${crypto.randomUUID()}`,
            type: chartType,
            title,
            data: chartData,
          };

          updateAssistantMessage(threadId, assistantMessageId, message => ({
            ...message,
            charts: [...(message.charts ?? []), chart],
          }));
          pushEvent("Rendering chart");
          return;
        }

        updateConnectorsForToolCall(data.name, data.args);
        pushEvent(`Calling ${data.name}`);
        return;
      }

      if (data.type === "tool_result") {
        updateConnectorsForToolResult(data.name, data.summary);
        pushEvent(`${data.name}: ${data.summary}`);
        return;
      }

      if (data.type === "content_chunk") {
        updateAssistantMessage(threadId, assistantMessageId, message => ({
          ...message,
          content: `${message.content}${data.chunk}`,
        }));
        return;
      }

      if (data.type === "complete") {
        completed = true;
        finalizeAssistantMessage(threadId, assistantMessageId, prompt, data.response);
        ws.close();
        return;
      }

      if (data.type === "error") {
        completed = true;
        fallback();
        ws.close();
      }
    };

    ws.onerror = () => {
      if (!completed) {
        completed = true;
        fallback();
      }
      ws.close();
    };

    ws.onclose = () => {
      if (!completed) {
        completed = true;
        fallback();
      }
    };
  };

  const askAi = (prompt: string, scope: AnalysisScope, threadId?: string, regenerate = false): string => {
    const activeThreadId = threadId ?? state.chatThreads[0]?.id ?? "thread-main";
    const history = resolveThreadHistory(activeThreadId);
    const assistantMessageId = `msg-${crypto.randomUUID()}`;

    try {
      update(prev => {
        const now = new Date().toISOString();
        const nextThreads = [...prev.chatThreads];
        let thread = nextThreads.find(item => item.id === activeThreadId);

        if (!thread) {
          thread = {
            id: activeThreadId,
            title: prompt,
            scope,
            createdAt: now,
            updatedAt: now,
            messages: [],
          };
          nextThreads.unshift(thread);
        }

        thread.scope = scope;

        const userMessage: ChatMessage = {
          id: `msg-${crypto.randomUUID()}`,
          role: "user",
          content: prompt,
          createdAt: now,
        };

        const assistantMessage: ChatMessage = {
          id: assistantMessageId,
          role: "assistant",
          content: "Sure — give me a moment while I pull the latest data.\n\n",
          createdAt: now,
          isStreaming: true,
          toolEvents: ["Connecting to agent"],
          connectorEvents: [],
        };

        thread.messages = [...thread.messages, userMessage, assistantMessage];
        thread.updatedAt = now;
        if (!thread.title || thread.title === "New conversation") {
          thread.title = prompt.slice(0, 40);
        }

        return {
          ...prev,
          pendingPrompt: "",
          chatThreads: nextThreads,
        };
      });

      void requestAgentReply(prompt, scope, activeThreadId, assistantMessageId, regenerate, history);
    } catch {
      const fallback = buildAiResponse(state, prompt, scope, regenerate ? 1 : 0);
      finalizeAssistantMessage(activeThreadId, assistantMessageId, prompt, fallback);
    }

    return activeThreadId;
  };

  const toggleFavorite = (threadId: string, messageId: string, prompt: string, response: string) => {
    update(prev => {
      const existing = prev.favorites.find(item => item.id === messageId);
      if (existing) {
        return {
          ...prev,
          favorites: prev.favorites.filter(item => item.id !== messageId),
        };
      }

      return {
        ...prev,
        favorites: [
          {
            id: messageId,
            threadId,
            prompt,
            response,
            createdAt: new Date().toISOString(),
          },
          ...prev.favorites,
        ],
      };
    });
  };

  const convertPromptToReport = (prompt: string) => {
    update(prev => ({ ...prev, pendingPrompt: prompt }));
  };

  const saveReport = (input: Omit<ScheduleReport, "id" | "createdAt" | "nextRunAt" | "scheduleRule"> & { id?: string }) => {
    update(prev => {
      const parsed = parseScheduleText(input.scheduleText);
      const existing = prev.scheduledReports.find(report => report.id === input.id);

      const report: ScheduleReport = {
        ...input,
        id: input.id ?? `report-${crypto.randomUUID()}`,
        createdAt: existing?.createdAt ?? new Date().toISOString(),
        nextRunAt: parsed.nextRun,
        scheduleRule: parsed.rule,
      };

      if (existing) {
        return {
          ...prev,
          pendingPrompt: "",
          scheduledReports: prev.scheduledReports.map(item => (item.id === report.id ? report : item)),
        };
      }

      return {
        ...prev,
        pendingPrompt: "",
        scheduledReports: [report, ...prev.scheduledReports],
      };
    });
  };

  const deleteReport = (id: string) => {
    update(prev => ({ ...prev, scheduledReports: prev.scheduledReports.filter(report => report.id !== id) }));
  };

  const saveInvite = (email: string, permissions: Record<string, PermissionLevel[]>) => {
    const invite: TeamInvite = {
      id: `invite-${crypto.randomUUID()}`,
      email,
      sentAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(),
      permissions,
    };

    update(prev => ({ ...prev, teamInvites: [invite, ...prev.teamInvites] }));
  };

  const cancelInvite = (id: string) => {
    update(prev => ({ ...prev, teamInvites: prev.teamInvites.filter(invite => invite.id !== id) }));
  };

  const connectPlatform = async (platform: PlatformType, connectionName: string) => {
    if (platform === "xero" || platform === "procore") {
      const response = await fetch(`/api/integrations/${platform}/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ connectionName, returnTo: "/dashboard/integrations" }),
      });

      const payload = (await response.json()) as { authUrl?: string; error?: string };
      if (!response.ok || !payload.authUrl) {
        throw new Error(payload.error ?? `Failed to start ${platform} connection`);
      }

      window.location.assign(payload.authUrl);
      return;
    }

    const tenantId = `${platform}-${Math.floor(Math.random() * 99999)}`;
    const connection: Connection = {
      id: `conn-${platform}-${crypto.randomUUID()}`,
      platform,
      name: connectionName,
      tenantId,
      status: "healthy",
      groupIds: [],
      lastSyncAt: new Date().toISOString(),
    };

    update(prev => ({ ...prev, connections: [connection, ...prev.connections] }));
  };

  const completeOAuthConnection = async (input: {
    platform: Extract<PlatformType, "xero">;
    name: string;
    tenantId: string;
    tenantName?: string;
    tenantType?: string;
    oauth: NonNullable<Connection["oauth"]>;
  }): Promise<Connection> => {
    const response = await fetch("/api/integrations/xero/connections", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenantId: input.tenantId,
        tenantName: input.tenantName,
        tenantType: input.tenantType,
        name: input.name,
        oauth: input.oauth,
      }),
    });

    const payload = (await response.json().catch(() => ({}))) as { connection?: Connection; error?: string };
    if (!response.ok || !payload.connection) {
      throw new Error(payload.error ?? "Failed to save Xero connection");
    }

    const persisted: Connection = {
      ...payload.connection,
      oauth: input.oauth,
      metadata: {
        ...payload.connection.metadata,
        tenantName: input.tenantName,
        tenantType: input.tenantType,
      },
    };

    update(prev => {
      const existingIndex = prev.connections.findIndex(connection => connection.id === persisted.id);
      const nextConnections = existingIndex >= 0
        ? prev.connections.map((connection, index) => (index === existingIndex ? persisted : connection))
        : [persisted, ...prev.connections];
      return { ...prev, connections: nextConnections };
    });

    void syncStoredConnections();
    return persisted;
  };

  const syncConnection = async (id: string) => {
    update(prev => ({
      ...prev,
      connections: prev.connections.map(connection =>
        connection.id === id
          ? {
              ...connection,
              status: "healthy",
              lastSyncAt: new Date().toISOString(),
            }
          : connection,
      ),
    }));
  };

  const renameConnection = async (id: string, name: string) => {
    const connection = state.connections.find(item => item.id === id);
    if (connection && storedConnectorPlatforms.includes(connection.platform)) {
      const response = await fetch("/api/integrations/connections/rename", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id, name }),
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({ error: "Failed to rename connection" }))) as { error?: string };
        throw new Error(payload.error ?? "Failed to rename connection");
      }

      await syncStoredConnections();
      return;
    }

    update(prev => ({
      ...prev,
      connections: prev.connections.map(connection => (connection.id === id ? { ...connection, name } : connection)),
    }));
  };

  const disconnectConnection = async (id: string) => {
    const connection = state.connections.find(item => item.id === id);
    if (connection && storedConnectorPlatforms.includes(connection.platform)) {
      const response = await fetch(`/api/integrations/connections/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({ error: "Failed to delete connection" }))) as { error?: string };
        throw new Error(payload.error ?? "Failed to delete connection");
      }

      await syncStoredConnections();
    }

    update(prev => {
      const nextTeamInvites = prev.teamInvites.map(invite => {
        const nextPermissions = { ...invite.permissions };
        delete nextPermissions[id];
        return {
          ...invite,
          permissions: nextPermissions,
        };
      });

      return {
        ...prev,
        connections: prev.connections.filter(connection => connection.id !== id),
        teamInvites: nextTeamInvites,
        scheduledReports: prev.scheduledReports.map(report => (report.scope === id ? { ...report, scope: "all" } : report)),
        chatThreads: prev.chatThreads.map(thread => (thread.scope === id ? { ...thread, scope: "all" } : thread)),
      };
    });
  };

  const saveGroup = (input: { id?: GroupId; name: string; description?: string; connectionIds: ConnectionId[] }) => {
    update(prev => {
      const now = new Date().toISOString();
      const normalizedName = input.name.trim();
      if (!normalizedName) {
        return prev;
      }

      const existingGroup = input.id ? prev.groups.find(group => group.id === input.id) : undefined;
      const group: Group = {
        id: input.id ?? `group-${crypto.randomUUID()}`,
        name: normalizedName,
        description: input.description?.trim() || undefined,
        createdAt: existingGroup?.createdAt ?? now,
      };

      const nextGroups = existingGroup
        ? prev.groups.map(item => (item.id === group.id ? group : item))
        : [group, ...prev.groups];

      const selected = new Set(input.connectionIds);
      const nextConnections = prev.connections.map(connection => {
        const isSelected = selected.has(connection.id);
        const isCurrentGroup = connection.groupIds.includes(group.id);

        if (isSelected && !isCurrentGroup) {
          return { ...connection, groupIds: [...connection.groupIds, group.id] };
        }

        if (!isSelected && isCurrentGroup) {
          return { ...connection, groupIds: connection.groupIds.filter(groupId => groupId !== group.id) };
        }

        return connection;
      });

      return {
        ...prev,
        groups: nextGroups,
        connections: nextConnections,
      };
    });
  };

  const deleteGroup = (id: GroupId) => {
    update(prev => ({
      ...prev,
      groups: prev.groups.filter(group => group.id !== id),
      connections: prev.connections.map(connection =>
        connection.groupIds.includes(id)
          ? { ...connection, groupIds: connection.groupIds.filter(groupId => groupId !== id) }
          : connection,
      ),
      scheduledReports: prev.scheduledReports.map(report => (report.scope === id ? { ...report, scope: "all" } : report)),
      chatThreads: prev.chatThreads.map(thread => (thread.scope === id ? { ...thread, scope: "all" } : thread)),
    }));
  };

  const deleteWaitlist = (id: string) => {
    update(prev => ({ ...prev, waitlistEntries: prev.waitlistEntries.filter(entry => entry.id !== id) }));
  };

  const deleteUser = (id: string) => {
    update(prev => ({ ...prev, users: prev.users.filter(user => user.id !== id) }));
  };

  const toggleUserStatus = (id: string) => {
    update(prev => ({
      ...prev,
      users: prev.users.map(user =>
        user.id === id ? { ...user, status: user.status === "active" ? "disabled" : "active" } : user,
      ),
    }));
  };

  const setTheme = (activeTheme: AppTheme) => {
    update(prev => ({ ...prev, activeTheme }));
  };

  return (
    <DemoContext.Provider
      value={{
        state,
        currentUser,
        activeTheme: state.activeTheme,
        loginAsRole,
        logout,
        resetDemo,
        setPendingPrompt,
        setSelectedRange,
        clearChat,
        askAi,
        toggleFavorite,
        convertPromptToReport,
        saveReport,
        deleteReport,
        saveInvite,
        cancelInvite,
        connectPlatform,
        completeOAuthConnection,
        syncConnection,
        renameConnection,
        disconnectConnection,
        saveGroup,
        deleteGroup,
        deleteWaitlist,
        deleteUser,
        toggleUserStatus,
        setTheme,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
};

export const useDemo = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error("useDemo must be used inside DemoProvider");
  }

  return context;
};
